/**
 * Merges consecutive time slots on the same court into single bookings.
 * E.g., Bana 2 18:00-18:30 + Bana 2 18:30-19:00 → Bana 2 18:00-19:00
 */

export interface SelectedSlot {
  courtId: string;
  courtName: string;
  startTime: string; // ISO
  endTime: string;   // ISO
  price: number;
}

export interface MergedBooking {
  courtId: string;
  courtName: string;
  startTime: string; // earliest start
  endTime: string;   // latest end
  totalPrice: number;
  slotCount: number;
  /** Original slot start times, for removing from selection */
  originalStartTimes: string[];
}

export function mergeConsecutiveSlots(slots: SelectedSlot[]): MergedBooking[] {
  if (slots.length === 0) return [];

  // Group by courtId
  const byCourtId = new Map<string, SelectedSlot[]>();
  for (const slot of slots) {
    const existing = byCourtId.get(slot.courtId) ?? [];
    existing.push(slot);
    byCourtId.set(slot.courtId, existing);
  }

  const merged: MergedBooking[] = [];

  for (const [, courtSlots] of byCourtId) {
    // Sort by startTime ascending
    const sorted = [...courtSlots].sort(
      (a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime()
    );

    let current: MergedBooking = {
      courtId: sorted[0].courtId,
      courtName: sorted[0].courtName,
      startTime: sorted[0].startTime,
      endTime: sorted[0].endTime,
      totalPrice: sorted[0].price,
      slotCount: 1,
      originalStartTimes: [sorted[0].startTime],
    };

    for (let i = 1; i < sorted.length; i++) {
      if (sorted[i].startTime === current.endTime) {
        // Consecutive — extend current
        current.endTime = sorted[i].endTime;
        current.totalPrice += sorted[i].price;
        current.slotCount += 1;
        current.originalStartTimes.push(sorted[i].startTime);
      } else {
        // Gap — push current and start new
        merged.push(current);
        current = {
          courtId: sorted[i].courtId,
          courtName: sorted[i].courtName,
          startTime: sorted[i].startTime,
          endTime: sorted[i].endTime,
          totalPrice: sorted[i].price,
          slotCount: 1,
          originalStartTimes: [sorted[i].startTime],
        };
      }
    }
    merged.push(current);
  }

  // Sort: by startTime, then by courtName
  return merged.sort((a, b) => {
    const timeDiff = new Date(a.startTime).getTime() - new Date(b.startTime).getTime();
    if (timeDiff !== 0) return timeDiff;
    return a.courtName.localeCompare(b.courtName);
  });
}
